import {
  Flashlight,
  Thermometer,
  Sun,
  Settings,
  LineChart,
  ShieldCheck,
  ArrowRight,
  Cable,
  FileCode,
  Cpu,
  ArrowUpRight
} from 'lucide-react';

export default function Services() {
  const services = [
    {
      icon: <Flashlight className="w-6 h-6" />,
      title: 'Ingeniería Eléctrica',
      description: 'Diseño y ejecución de proyectos BT/MT, tableros y canalizaciones bajo normativa SEC.',
      color: 'bg-primary',
      badge: 'Proyectos Certificados'
    },
    {
      icon: <Thermometer className="w-6 h-6" />,
      title: 'Bombas de Calor',
      description: 'Electrificación térmica industrial con control inteligente y alta eficiencia.',
      color: 'bg-accent',
      badge: 'I+D Propia'
    },
    {
      icon: <Sun className="w-6 h-6" />,
      title: 'Energía Solar',
      description: 'Sistemas fotovoltaicos industriales on/off-grid y nodos solares para minería.',
      color: 'bg-[#00A8E8]',
      badge: 'Autonomía Total'
    },
    {
      icon: <Settings className="w-6 h-6" />,
      title: 'Optimización Operativa',
      description: 'Planes preventivos y diagnósticos avanzados para asegurar continuidad.',
      color: 'bg-slate-700',
      badge: '24/7 Support'
    },
    {
      icon: <LineChart className="w-6 h-6" />,
      title: 'Auditorías ISO 50001',
      description: 'Análisis profundo y planes de mejora para maximizar el desempeño energético.',
      color: 'bg-blue-950',
      badge: 'ESG Compliance'
    },
    {
      icon: <ShieldCheck className="w-6 h-6" />,
      title: 'Certificaciones SEC',
      description: 'Gestión técnica TE1, TE2, TE4 y cumplimiento normativo integral.',
      color: 'bg-emerald-900',
      badge: 'Gestión Legal'
    }
  ];

  const integrationDetails = [
    {
      title: 'Ingeniería & Diseño',
      items: ['Planimetría y Diagramas', 'Cubicación Técnica', 'Especificaciones'],
      icon: <FileCode className="w-5 h-5" />
    },
    {
      title: 'Telecomunicaciones',
      items: ['Fibra Óptica Industrial', 'Certificación CAT 6A', 'Redes Críticas'],
      icon: <Cable className="w-5 h-5" />
    },
    {
      title: 'Soluciones Móviles',
      items: ['Carros Solares CCTV', 'Torres Iluminación', 'Nodos Mina'],
      icon: <Sun className="w-5 h-5" />
    }
  ];

  return (
    <section id="servicios" className="py-32 bg-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[1px] bg-gradient-to-r from-transparent via-slate-200 to-transparent" />

      <div className="section-container relative z-10">
        <div className="flex flex-col md:flex-row items-end justify-between mb-24 gap-8">
          <div className="max-w-3xl" data-aos="fade-right">
            <div className="badge-premium mb-6">
              <Cpu size={14} className="text-accent" />
              <span>Ecosistema de Soluciones</span>
            </div>
            <h2 className="text-4xl md:text-7xl font-black text-primary-dark tracking-tighter leading-tight">
              Ingeniería <br />
              <span className="text-slate-400 italic">Sin Fronteras</span>
            </h2>
          </div>
          <p className="text-xl text-slate-500 font-light max-w-sm mb-2" data-aos="fade-left">
            Desarrollamos infraestructura crítica que impulsa la competitividad y sostenibilidad industrial.
          </p>
        </div>

        {/* Core Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-40">
          {services.map((service, index) => (
            <div key={index} className="group p-10 rounded-[3rem] bg-slate-50 border border-slate-100 hover:bg-white hover:shadow-premium transition-all duration-500 flex flex-col">
              <div className={`w-16 h-16 rounded-2xl ${service.color} text-white flex items-center justify-center mb-10 shadow-lg group-hover:scale-110 transition-transform`}>
                {service.icon}
              </div>
              <div className="mb-4">
                <span className="text-[10px] font-black text-accent uppercase tracking-[0.2em]">{service.badge}</span>
              </div>
              <h3 className="text-2xl font-black text-primary-dark mb-4 tracking-tight leading-tight">{service.title}</h3>
              <p className="text-slate-500 font-light text-sm leading-relaxed mb-10 flex-grow">{service.description}</p>

              <div className="pt-8 border-t border-slate-200/50 flex items-center justify-between">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Technical Division</span>
                <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center border border-slate-100 text-primary-dark group-hover:bg-primary-dark group-hover:text-white group-hover:border-primary-dark transition-all">
                  <ArrowUpRight size={14} />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Integration Section */}
        <div className="grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7 bg-primary-dark rounded-[4rem] p-12 lg:p-20 relative overflow-hidden group">
            <div className="absolute inset-0 bg-mesh-gradient opacity-20 group-hover:opacity-40 transition-opacity" />
            <div className="relative z-10 h-full flex flex-col">
              <div className="badge-premium bg-white/10 text-white border-white/20 mb-10">
                <Settings size={14} />
                <span>Fabricación Propia</span>
              </div>
              <h3 className="text-4xl md:text-6xl font-black text-white mb-10 tracking-tighter leading-[1.1]">
                Manufactura y <br />
                <span className="text-accent underline decoration-accent/20 underline-offset-8">Despliegue</span>
              </h3>
              <p className="text-white/60 text-lg font-light mb-12 max-w-xl">
                Operamos nuestro propio centro de fabricación de tableros eléctricos y soluciones móviles, garantizando control total sobre la calidad y los plazos de entrega.
              </p>

              <div className="grid grid-cols-2 gap-4 mt-auto">
                {['Tableros BT/MT', 'Nodos Litio', 'Luminarias LED', 'Nodos CCTV'].map((item, i) => (
                  <div key={i} className="px-6 py-4 rounded-2xl bg-white/5 border border-white/10 flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-accent" />
                    <span className="text-sm font-bold text-white/80">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 flex flex-col gap-8">
            <div className="flex-1 bg-slate-50 border border-slate-100 rounded-[4rem] p-12 hover:bg-white hover:shadow-premium transition-all duration-500">
              <h4 className="text-2xl font-black text-primary-dark mb-10 tracking-tight">Especialidades <br /> <span className="text-slate-400">Técnicas</span></h4>
              <div className="space-y-10">
                {integrationDetails.map((area, i) => (
                  <div key={i} className="flex gap-6">
                    <div className="w-12 h-12 rounded-xl bg-white shadow-sm flex items-center justify-center text-primary-dark flex-shrink-0">
                      {area.icon}
                    </div>
                    <div>
                      <h5 className="font-bold text-primary-dark text-sm uppercase tracking-widest mb-3">{area.title}</h5>
                      <div className="flex flex-wrap gap-2">
                        {area.items.map((item, j) => (
                          <span key={j} className="text-[10px] font-bold text-slate-400 bg-white border border-slate-100 px-3 py-1 rounded-full uppercase tracking-tighter">
                            {item}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-accent rounded-[3rem] p-8 flex items-center justify-between group cursor-pointer hover:shadow-xl hover:shadow-accent/20 transition-all">
              <div className="text-white">
                <div className="text-[10px] font-bold uppercase tracking-widest mb-1 opacity-70">Documentos</div>
                <div className="text-lg font-black tracking-tight">Catálogo Técnico 2024</div>
              </div>
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center text-white group-hover:bg-white group-hover:text-accent transition-all">
                <ArrowRight />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}


